// Hear the tracks before they go up: the checked ones together, or one alone.
//
// One <audio> per track, made on first play, all started from 0 together.
// While the mix plays, (un)checking a track mutes it in place, so a producer
// hears exactly what the seed will be. Element-per-track keeps memory to what
// the browser streams; a few ms of drift between them is fine for a preview.

import { useCallback, useEffect, useRef, useState } from "react";

import type { IntakeFile } from "@/lib/web-upload/intake";
import { previewUrl } from "@/lib/web-upload/preview-source";
import { registerExternalPreview } from "@web-shared/lib/external-previews";

/** What's playing: the checked mix, one track by index, or nothing. */
export type PreviewMode = "mix" | number | null;

export interface StemPreview {
  mode: PreviewMode;
  /** True while the files are being made playable. */
  loading: boolean;
  failed: boolean;
  playMix: () => void;
  playOne: (index: number) => void;
  stop: () => void;
}

export function useStemPreview(files: IntakeFile[], included: boolean[]): StemPreview {
  const [mode, setMode] = useState<PreviewMode>(null);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const els = useRef<Array<HTMLAudioElement | null>>([]);
  const urls = useRef<string[]>([]);
  const run = useRef(0);

  const audible = useCallback(
    (m: PreviewMode, i: number) => (m === "mix" ? included[i] === true : m === i),
    [included],
  );

  // A checkbox mutes or unmutes its track in the mix that's playing.
  useEffect(() => {
    els.current.forEach((el, i) => {
      if (el) el.muted = !audible(mode, i);
    });
  }, [audible, mode]);

  const stop = useCallback(() => {
    run.current++;
    for (const el of els.current) el?.pause();
    setMode(null);
    setLoading(false);
  }, []);

  const elementFor = async (i: number): Promise<HTMLAudioElement> => {
    const have = els.current[i];
    if (have) return have;
    const url = await previewUrl(files[i]!);
    urls.current.push(url);
    const el = new Audio(url);
    el.preload = "auto";
    els.current[i] = el;
    return el;
  };

  const start = async (next: Exclude<PreviewMode, null>) => {
    const id = ++run.current;
    for (const el of els.current) el?.pause();
    setFailed(false);
    setLoading(true);
    setMode(next);
    try {
      const wanted = next === "mix" ? files.map((_, i) => i) : [next];
      const ready = await Promise.all(wanted.map((i) => elementFor(i).then((el) => [i, el] as const)));
      if (id !== run.current) return;
      for (const [i, el] of ready) {
        el.currentTime = 0;
        el.muted = !audible(next, i);
      }
      // The preview ends with the last track to finish.
      for (const [, el] of ready) {
        el.onended = () => {
          if (id === run.current && ready.every(([, e]) => e.ended)) stop();
        };
      }
      // The page's Stop reaches this preview too.
      registerExternalPreview(stop);
      await Promise.all(ready.map(([, el]) => el.play()));
    } catch {
      if (id === run.current) {
        setFailed(true);
        stop();
      }
      return;
    }
    if (id === run.current) setLoading(false);
  };

  useEffect(
    () => () => {
      run.current++;
      for (const el of els.current) el?.pause();
      for (const url of urls.current) URL.revokeObjectURL(url);
    },
    [],
  );

  return {
    mode,
    loading,
    failed,
    playMix: () => void start("mix"),
    playOne: (i) => void start(i),
    stop,
  };
}
